import { useEffect, useRef, useState } from "react";
import { Send, Bot, FlaskConical, Loader2 } from "lucide-react";
import { adminApi, fmtDate } from "../lib/api";
import { Card, Title, Toggle, Badge, act } from "./kit";

function Bubble({ m }) {
  const mine = m.from !== "user";
  return (
    <div className={`flex ${mine ? "justify-end" : ""}`}>
      <div className={`max-w-[80%] rounded-3xl px-4 py-2.5 text-sm whitespace-pre-line ${m.from === "user" ? "bg-white border border-[#f1e4ea]" : m.from === "bot" ? "bg-[var(--teal-soft)]" : "bg-[var(--pink)] text-white"}`} data-testid={`chat-msg-${m.from}`}>
        {m.from === "bot" && <span className="flex items-center gap-1 text-[10px] font-bold uppercase mb-1"><Bot className="w-3 h-3" /> robot</span>}
        {m.text}
        <p className="text-[10px] opacity-60 mt-1">{new Date(m.at).toLocaleString("cs-CZ")}</p>
      </div>
    </div>
  );
}

export default function Messenger() {
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef();
  const loadConvs = () => adminApi.get("/messenger/conversations").then((r) => setConvs(r.data));
  const open = async (c) => {
    setSel(c);
    const { data } = await adminApi.get(`/messenger/conversations/${c.id}`);
    setMsgs(data);
  };
  useEffect(() => {
    loadConvs();
    const t = setInterval(loadConvs, 15000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      if (!sel || sel.id === "test-chat") {
        await act(() => adminApi.post("/messenger/test", { text }));
        const c = { id: "test-chat", name: "Testovací chat", source: "test", bot_enabled: true };
        await open(c);
      } else {
        await act(() => adminApi.post(`/messenger/conversations/${sel.id}/reply`, { text }));
        await open(sel);
      }
      setText("");
      loadConvs();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="admin-messenger">
      <Title>Messenger robot</Title>
      <p className="text-sm text-[var(--muted)] mb-4 max-w-3xl">Zprávy z Facebook Messengeru se zobrazují zde jako chat. Robot (AI) odpovídá na dotazy, doporučuje materiály a posílá odkazy na web. Robota můžete vypnout pro každou konverzaci a odpovědět ručně. V „Testovacím chatu“ si robota vyzkoušíte bez Facebooku.</p>
      <div className="grid lg:grid-cols-[300px_1fr] gap-4 h-[70vh]">
        <Card className="!p-2 overflow-y-auto">
          <button onClick={() => open({ id: "test-chat", name: "Testovací chat", source: "test", bot_enabled: true })} data-testid="messenger-test-chat-btn" className="w-full flex items-center gap-2 p-3 rounded-2xl hover:bg-[var(--blush)] font-bold text-sm"><FlaskConical className="w-4 h-4 text-[var(--teal)]" /> Testovací chat</button>
          {convs.filter((c) => c.id !== "test-chat").map((c) => (
            <button key={c.id} onClick={() => open(c)} data-testid={`conversation-${c.id}`} className={`w-full text-left p-3 rounded-2xl text-sm ${sel?.id === c.id ? "bg-[var(--pink-soft)]" : "hover:bg-[var(--blush)]"}`}>
              <div className="flex justify-between"><span className="font-bold">{c.name}</span>{c.unread > 0 && <Badge>{c.unread}</Badge>}</div>
              <p className="text-xs text-[var(--muted)] truncate">{c.last_message}</p><p className="text-[10px] text-[var(--muted)]">{fmtDate(c.updated_at)}</p>
            </button>
          ))}
          {convs.filter((c) => c.id !== "test-chat").length === 0 && <p className="text-xs text-[var(--muted)] p-3">Zatím žádné zprávy z Messengeru. Po propojení stránky přes Meta se zde objeví.</p>}
        </Card>
        <Card className="!p-0 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-[#f1e4ea]">
            <p className="font-display text-lg">{sel?.name || "Testovací chat"}</p>
            {sel && sel.id !== "test-chat" && <Toggle checked={sel.bot_enabled !== false} onChange={async (v) => { await adminApi.put(`/messenger/conversations/${sel.id}`, { bot_enabled: v }); setSel({ ...sel, bot_enabled: v }); }} label="Robot odpovídá" testid="conversation-bot-toggle" />}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#fdfbfc]" data-testid="chat-messages">
            {msgs.map((m) => <Bubble key={m.id} m={m} />)}
            {busy && <div className="flex justify-end"><Loader2 className="w-5 h-5 animate-spin text-[var(--teal)]" /></div>}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-[#f1e4ea] flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={!sel || sel.id === "test-chat" ? "Napište dotaz jako zákazník…" : "Ruční odpověď zákazníkovi…"} className="field !rounded-full" data-testid="chat-input" />
            <button disabled={busy} className="btn-pink !px-4" data-testid="chat-send-btn"><Send className="w-4 h-4" /></button>
          </form>
        </Card>
      </div>
    </div>
  );
}
